// Month arithmetic and bar layout for the availability tape chart. Pure functions, kept out of the
// component so the lane packing is readable and testable on its own.
import type {
  ItemOccupancyKind,
  WaterCalendarClaim,
  WaterCalendarPeriod,
  WaterCalendarReservation,
  WaterCalendarRow,
  WaterCalendarStay,
} from '@models/water-calendar';

export function addDays(date: Date, days: number): Date {
  const shifted = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

/**
 * `yyyy-MM-dd` from local date parts — the form every date in the API's payload takes, so the chart can
 * compare dates as plain strings.
 *
 * Deliberately not `@utils/date-only`'s `toDateOnly`, which returns `string | null` for the benefit of
 * nullable form controls: every date in here exists, and laundering that null away with `as string` at each
 * call site is a non-null assertion wearing a disguise.
 */
function isoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date: Date): Date {
  // Day 0 of the next month is the last day of this one, which also handles February and leap years.
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/** The whole calendar month `anchor` falls in — the chart's window. */
export function monthWindow(anchor: Date): { from: Date; to: Date } {
  return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
}

/** One month forward or back. Anchored to the 1st, so stepping off the 31st cannot skip a short month. */
export function stepMonth(anchor: Date, direction: 1 | -1): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
}

/** Every date in `[from, to]` as `yyyy-MM-dd` — the chart's ruler. */
export function datesBetween(from: Date, to: Date): string[] {
  const dates: string[] = [];
  for (let date = from; date <= to; date = addDays(date, 1)) dates.push(isoDate(date));
  return dates;
}

// --- Lane packing ------------------------------------------------------------------------------

/**
 * Greedy lane packing: each bar goes in the first lane with room for it, so overlapping bars stack into
 * as many sub-lanes as the deepest overlap.
 *
 * Shared by the season lane and the item rows, which want identical behaviour for the same reason —
 * overlaps are real and meaningful (two seasons; a double booking), so they are shown stacked rather
 * than resolved into one value per day. A doubled row *is* the conflict indicator.
 *
 * Placed left to right, longest first at equal starts, which keeps the long bar on the top lane with
 * short ones stacked beneath rather than the reverse.
 */
function packLanes<T extends { startIndex: number; span: number }>(bars: T[]): T[][] {
  const sorted = [...bars].sort((a, b) => a.startIndex - b.startIndex || b.span - a.span);

  const lanes: T[][] = [];
  for (const bar of sorted) {
    const lane = lanes.find((existing) => {
      const last = existing[existing.length - 1];
      return last.startIndex + last.span <= bar.startIndex;
    });
    if (lane) lane.push(bar);
    else lanes.push([bar]);
  }
  return lanes;
}

// --- Season lane -------------------------------------------------------------------------------

/** One season period drawn as a bar across the days it covers. */
export interface PeriodBar {
  period: WaterCalendarPeriod;
  /** 0-based column index into the window's dates. */
  startIndex: number;
  /** Columns covered, at least 1. */
  span: number;
}

/**
 * The season lane's bars, packed into sub-lanes. Nothing stops periods overlapping, and a closure
 * sitting inside a high season is a normal, meaningful state — the closure blocks, the season still
 * prices the open days — so both keep their own bar.
 *
 * Over a month window one authored period can produce several spans (it is keyed by ISO week, so a
 * fortnight-on/fortnight-off pattern is two bars); the API has already clipped each to the window.
 */
export function packPeriodLanes(periods: WaterCalendarPeriod[], dates: string[]): PeriodBar[][] {
  return packLanes(
    periods
      .map((period) => {
        const startIndex = dates.indexOf(period.from);
        const endIndex = dates.indexOf(period.to);
        if (startIndex < 0 || endIndex < 0) return null;
        return { period, startIndex, span: endIndex - startIndex + 1 };
      })
      .filter((bar): bar is PeriodBar => bar !== null),
  );
}

/**
 * A lane as cells the template renders into one table row: the bars, with the gaps between them as
 * empty cells. Spans become colspans, so every lane row adds up to the window's column count.
 */
export interface LaneCell {
  bar: PeriodBar | null;
  span: number;
}

export function laneCells(lane: PeriodBar[], dayCount: number): LaneCell[] {
  const cells: LaneCell[] = [];
  let column = 0;
  for (const bar of lane) {
    if (bar.startIndex > column) cells.push({ bar: null, span: bar.startIndex - column });
    cells.push({ bar, span: bar.span });
    column = bar.startIndex + bar.span;
  }
  if (column < dayCount) cells.push({ bar: null, span: dayCount - column });
  return cells;
}

// --- Item rows ---------------------------------------------------------------------------------

/**
 * One reservation line's stay as a single bar across the nights it holds.
 *
 * A stay is one bar, not one cell per night: the reservation number, the customer name and the hover
 * detail belong to the stay, not to each of its nights. Drawing them as ranges is what makes the
 * changeover rhythm and the gaps between bookings legible at a glance.
 */
export interface ClaimBar {
  claim: WaterCalendarClaim;
  /** 0-based column index into the window's dates. */
  startIndex: number;
  /** Columns covered, at least 1. */
  span: number;
  /**
   * Distinct reservations compete for this item on at least one of the bar's nights — genuine
   * over-capacity, as opposed to one reservation holding the same item on two of its own lines (a
   * group split, or a companion line). Both stack into sub-lanes; only this earns the red outline.
   */
  isConflict: boolean;
  /** First and last date the bar covers. */
  from: string;
  to: string;

  // Everything below is resolved here rather than by the template, so a bar is data to render and not a
  // set of functions to call. The template used to ask four methods per bar per change-detection pass —
  // and any hover on any bar triggers one — two of which allocated dates on every call.

  /** Reservation number and customer, ready to print. */
  label: string;
  /**
   * The reservation is cancelled or deleted, so these nights should have been released — the resync has not
   * caught up. A claim whose reservation is missing entirely counts as stale too: the row outlived it.
   */
  isStale: boolean;
  /**
   * The stay runs on beyond this bar, so that end is squared off to say it was cut by the window rather
   * than that the booking ends there. Answered from the stay's own dates, not from the bar touching the
   * window edge — a stay that genuinely starts on the 1st must stay rounded.
   */
  continuesBefore: boolean;
  continuesAfter: boolean;

  // A raffle block occupies several sub-lanes at once — one per rod place — and so is emitted as one bar
  // per lane. These say where in that stack a bar sits, which is what lets the lanes render as a single
  // block: only the top one is labelled and carries the hover panel, and only the outer two are rounded.
  // Both are true on a named unit's bar, which is a one-lane block.

  /** This bar is the top of its block: it carries the label and the hover panel. */
  isBlockTop: boolean;
  /** This bar is the bottom of its block. */
  isBlockBottom: boolean;
  /** Rod places the block holds, shown beside the label. Absent on named units, which hold the whole item. */
  rods?: number;
}

/** The stay a claim's line belongs to. A stay can cover several lines — see {@link WaterCalendarStay}. */
export function stayOfClaim(
  reservation: WaterCalendarReservation | undefined,
  claim: WaterCalendarClaim,
): WaterCalendarStay | null {
  return (
    reservation?.stays.find((stay) =>
      stay.lines.some((line) => line.lineKind === claim.lineKind && line.lineId === claim.reservationLineId),
    ) ?? null
  );
}

/**
 * The parts of a bar that come from its reservation rather than from where it sits on the ruler.
 *
 * A missing reservation is not an error state to hide: the claim is real and the night really is occupied
 * as far as every other reader is concerned, so the bar still draws, labelled by id and marked stale.
 */
function describeClaim(
  claim: WaterCalendarClaim,
  from: string,
  to: string,
  reservations: ReadonlyMap<number, WaterCalendarReservation>,
): Pick<ClaimBar, 'label' | 'isStale' | 'continuesBefore' | 'continuesAfter'> {
  const reservation = reservations.get(claim.reservationId);
  const stay = stayOfClaim(reservation, claim);
  const number = reservation?.reservationNumber ?? `#${claim.reservationId}`;

  return {
    label: reservation?.customerName ? `${number} · ${reservation.customerName}` : number,
    isStale: reservation?.isStale ?? true,
    continuesBefore: stay !== null && stay.arrival < from,
    continuesAfter: stay !== null && lastNightOf(stay) > to,
  };
}

/**
 * A stay's last claimed night. `departure` is not one — the stay is half-open — and a day session is stored
 * with equal (or inverted) dates and still claims its arrival.
 */
function lastNightOf(stay: WaterCalendarStay): string {
  const [year, month, day] = stay.departure.split('-').map(Number);
  const dayBeforeDeparture = isoDate(new Date(year, month - 1, day - 1));
  return dayBeforeDeparture < stay.arrival ? stay.arrival : dayBeforeDeparture;
}

/**
 * Why an unclaimed night is nonetheless unavailable.
 *
 * - `closed` — a closed season period covers it.
 * - `whole-lake` — the lake is booked outright that night, which takes the individual units.
 * - `unit` — a unit is booked that night, which takes the whole-lake option.
 */
export type BlockedReason = 'closed' | 'whole-lake' | 'unit';

/** One cell of one sub-lane of one item row. `bar` is null on a plain track cell. */
export interface RowCell {
  /** A reservation bar spanning `span` columns. */
  bar: ClaimBar | null;
  /**
   * Why this cell's nights are unavailable, whether or not it also holds a bar. Without it the chart would
   * show every swim as free under an exclusive booking of the lake, and every night of a closed winter as
   * bookable — both true of the occupancy table, which answers only "is this item claimed", and both
   * misleading on a calendar someone books against.
   *
   * On a bar cell it is set only when every night the bar covers shares the one reason — see
   * {@link spanReason}. A closure never voids a reservation, so the two facts coexist: the wash is the
   * cell's background and the booking is a pill drawn over it.
   */
  blockedBy: BlockedReason | null;
  /** Columns covered. 1 except on a bar. */
  span: number;
}

/**
 * Raffle only: where the capacity line falls in a rod-place row.
 *
 * Rod places are an anonymous pool — the stored slot ordinals are an allocation artefact, freely different
 * from one night to the next — so a *lane* of such a row is not a rod place and must never be read as one.
 * It is only where the packer chose to draw a booking, held stable across its whole span so the block is a
 * rectangle rather than a zigzag.
 */
export interface RaffleScale {
  /**
   * Rod places the lake has. How many lanes are actually drawn is `lanes.length` — `capacity`, or more when
   * bookings spill past it — so it is not repeated here.
   */
  capacity: number;
  /**
   * The lane, counted from the top, under which the capacity line is drawn: the last one above capacity.
   * `null` unless something is oversold, since otherwise capacity is the row's own bottom edge and a line
   * there would be noise.
   *
   * Resolved here because this is where lanes are reversed for rendering — the knowledge that lane 0 is the
   * bottom belongs with the code that puts it there, not in a template inverting it again.
   */
  boundaryLaneFromTop: number | null;
}

/** One item row, as the sub-lanes of cells the template renders. */
export interface RowLayout {
  row: WaterCalendarRow;
  /** Top-down. One lane for a named unit unless its claims overlap; `capacity` or more for raffle. */
  lanes: RowCell[][];
  /** Raffle only, for the capacity line. Null on a named unit. */
  raffle: RaffleScale | null;
}

/**
 * Lay out every item row for the window: closures, the cross-item rules, and stays merged into bars.
 *
 * Three blocking rules, in precedence order. A closed night is unavailable on every row. Beyond that: a
 * unit night is unavailable under a whole-lake claim, and the whole-lake option is unavailable when any
 * unit night is claimed. Only *offered* rows take part in the cross-item pair — claims of a kind the
 * lake no longer offers block nothing, which is exactly why they are shown as retired.
 *
 * Raffle rows differ only in how tall they are: rod places are a shared pool, so the row is `capacity`
 * sub-lanes deep and a booking fills as many of them as it holds rods. Filled height is occupancy, the
 * space above is what is still free, and an oversold night reaches past the capacity line.
 *
 * **Every row — raffle included — is laid out as sub-lanes of table cells on the shared column grid.**
 * That is what keeps the columns lined up: the browser puts one cell per day in every row, so alignment is
 * structural. Do not give the raffle row a strip of its own with percentage offsets inside one `colspan`
 * cell — that is a second coordinate space, and it drifts out of step with the real columns.
 *
 * `reservations` is what makes each bar self-describing: its label, stale marker and squared-off ends are
 * resolved here, so the template renders fields instead of calling methods per bar per change detection.
 */
export function layoutRows(
  rows: WaterCalendarRow[],
  periods: WaterCalendarPeriod[],
  dates: string[],
  reservations: ReadonlyMap<number, WaterCalendarReservation>,
): RowLayout[] {
  const closed = new Set(
    dates.filter((date) => periods.some((period) => period.isClosed && period.from <= date && date <= period.to)),
  );

  const claimedDates = (predicate: (kind: ItemOccupancyKind) => boolean) =>
    new Set(
      rows
        .filter((row) => row.isOffered && predicate(row.kind))
        .flatMap((row) => row.nights.map((night) => night.date)),
    );

  const wholeLakeClaims = claimedDates((kind) => kind === 'Water');
  const unitClaims = claimedDates((kind) => kind !== 'Water');

  return rows.map((row) => {
    // A retired row is not part of the offering, so no cross-item rule reaches it either — its own
    // claims are all it has to say. Closures still apply: they are water-level.
    const crossItem = !row.isOffered
      ? null
      : row.kind === 'Water'
        ? { claims: unitClaims, reason: 'unit' as const }
        : { claims: wholeLakeClaims, reason: 'whole-lake' as const };

    const blockedAt = (date: string): BlockedReason | null => {
      if (closed.has(date)) return 'closed';
      if (crossItem?.claims.has(date)) return crossItem.reason;
      return null;
    };

    // Drawn on every lane and under bookings alike — a booked night in a closed period is both things
    // at once, so red track under a pill reads as "this booking sits in a closed period". Nothing is
    // suppressed, so a shared rod-place row still shows its free lanes as unavailable.
    if (row.kind === 'Raffle') {
      const { lanes, scale } = raffleLanes(row, dates, reservations);
      return { row, raffle: scale, lanes: lanes.map((lane) => spreadLane(lane, dates, blockedAt)) };
    }

    const lanes = packLanes(claimBars(row, dates, reservations));

    return {
      row,
      raffle: null,
      // A row with nothing booked is one lane of pure track, which is `spreadLane` over no bars — built
      // that way rather than mapped by hand so it merges its blocked runs like every other lane.
      lanes: (lanes.length === 0 ? [[]] : lanes).map((lane) => spreadLane(lane, dates, blockedAt)),
    };
  });
}

/**
 * Lay out a rod-place row: each booking takes the lowest band of adjacent free lanes tall enough for its
 * rods and clear for its whole span, and the row is returned as sub-lanes of bars, top lane first.
 *
 * First-fit over a two-dimensional (lane × day) reservation map, rather than the one-dimensional
 * {@link packLanes} — a raffle booking needs `slots` lanes at once, and needs them to be the *same*
 * lanes on every one of its nights, or the block would step up and down across the week. Adjacent, so a
 * multi-rod booking draws as one rectangle.
 *
 * Lanes grow past capacity when they have to. That is the point: an oversold night has nowhere legitimate
 * to go, so it goes above the line and is visible for what it is.
 *
 * A booking spanning several lanes is emitted once per lane, flagged so the stack renders as one block —
 * see {@link ClaimBar.isBlockTop}. That repetition is what buys the alignment: every lane is an ordinary
 * table row of per-day cells, so the browser puts the raffle row on the same column grid as every other.
 */
function raffleLanes(
  row: WaterCalendarRow,
  dates: string[],
  reservations: ReadonlyMap<number, WaterCalendarReservation>,
): { lanes: ClaimBar[][]; scale: RaffleScale } {
  // occupied[lane][column] — grown on demand, so the row is only as tall as it needs to be.
  const occupied: boolean[][] = [];
  const laneIsFree = (lane: number, startIndex: number, span: number) =>
    occupied[lane] === undefined || !occupied[lane].slice(startIndex, startIndex + span).some(Boolean);
  const allLanesFree = (lane: number, run: ClaimRun) => {
    for (let l = lane; l < lane + run.slots; l++) {
      if (!laneIsFree(l, run.startIndex, run.span)) return false;
    }
    return true;
  };

  const blocks = claimRuns(row, dates)
    // Longest and tallest first at equal starts: a wide or many-rod booking placed early keeps the row
    // compact, where letting a one-nighter take lane 0 first would push it upward for no reason.
    .sort((a, b) => a.startIndex - b.startIndex || b.span - a.span || b.slots - a.slots)
    .map((run) => {
      let lane = 0;
      while (!allLanesFree(lane, run)) lane++;

      for (let l = lane; l < lane + run.slots; l++) {
        occupied[l] ??= [];
        for (let c = run.startIndex; c < run.startIndex + run.span; c++) occupied[l][c] = true;
      }

      return { run, lane };
    });

  const highestLaneUsed = blocks.reduce((highest, { run, lane }) => Math.max(highest, lane + run.slots), 0);
  const laneCount = Math.max(row.capacity, highestLaneUsed, 1);

  // Lane 0 is the bottom of the row, so the lanes come out in reverse: bookings sit at the bottom and the
  // rod places still free are the empty lanes above them.
  const lanes: ClaimBar[][] = [];
  for (let lane = laneCount - 1; lane >= 0; lane--) {
    lanes.push(
      blocks
        .filter(({ run, lane: bottom }) => lane >= bottom && lane < bottom + run.slots)
        .map(({ run, lane: bottom }) => {
          const from = dates[run.startIndex];
          const to = dates[run.startIndex + run.span - 1];
          return {
            claim: run.claim,
            startIndex: run.startIndex,
            span: run.span,
            // Over capacity is a property of the block, not of one reservation competing with another, so
            // it reuses the conflict outline rather than inventing a second warning.
            isConflict: bottom + run.slots > row.capacity,
            from,
            to,
            isBlockTop: lane === bottom + run.slots - 1,
            isBlockBottom: lane === bottom,
            rods: run.slots,
            ...describeClaim(run.claim, from, to, reservations),
          };
        }),
    );
  }

  return {
    lanes,
    scale: {
      capacity: row.capacity,
      boundaryLaneFromTop: laneCount > row.capacity ? laneCount - 1 - row.capacity : null,
    },
  };
}

/** One reservation line's unbroken run of claimed days, with how many slots it holds across them. */
interface ClaimRun {
  claim: WaterCalendarClaim;
  startIndex: number;
  span: number;
  /** Rod places held. The maximum over the run, so a bar never under-draws what is booked. */
  slots: number;
  /** Distinct reservations compete on at least one of these days. */
  conflict: boolean;
}

/**
 * Split one row's claims into per-line runs of consecutive days. Shared by both geometries: a named
 * unit's run becomes a one-lane bar, a raffle run a block `slots` lanes tall.
 */
function claimRuns(row: WaterCalendarRow, dates: string[]): ClaimRun[] {
  const indexOfDate = new Map(dates.map((date, index) => [date, index]));

  // Per reservation *line*: two lines of one reservation on the same item are two claims, and the line is
  // the authoritative one.
  const byLine = new Map<string, { claim: WaterCalendarClaim; index: number; conflict: boolean }[]>();
  for (const night of row.nights) {
    const index = indexOfDate.get(night.date);
    if (index === undefined) continue;

    // Distinct reservations, not claim count: one reservation on two of its own lines is a group split,
    // not two parties competing for the same item.
    const competing = new Set(night.reservations.map((claim) => claim.reservationId)).size > row.capacity;

    for (const claim of night.reservations) {
      const key = `${claim.reservationId}:${claim.lineKind}:${claim.reservationLineId}`;
      const entry = { claim, index, conflict: competing };
      const existing = byLine.get(key);
      if (existing) existing.push(entry);
      else byLine.set(key, [entry]);
    }
  }

  const runs: ClaimRun[] = [];
  for (const entries of byLine.values()) {
    entries.sort((a, b) => a.index - b.index);

    let run = [entries[0]];
    for (const entry of entries.slice(1)) {
      if (entry.index === run[run.length - 1].index + 1) run.push(entry);
      else {
        runs.push(toRun(run));
        run = [entry];
      }
    }
    runs.push(toRun(run));
  }
  return runs;
}

function toRun(run: { claim: WaterCalendarClaim; index: number; conflict: boolean }[]): ClaimRun {
  return {
    claim: run[0].claim,
    startIndex: run[0].index,
    span: run[run.length - 1].index - run[0].index + 1,
    slots: Math.max(...run.map((entry) => entry.claim.slots)),
    conflict: run.some((entry) => entry.conflict),
  };
}

/** A named unit's runs as one-lane bars — a block one lane tall, so it is both its own top and bottom. */
function claimBars(
  row: WaterCalendarRow,
  dates: string[],
  reservations: ReadonlyMap<number, WaterCalendarReservation>,
): ClaimBar[] {
  return claimRuns(row, dates).map((run) => {
    const from = dates[run.startIndex];
    const to = dates[run.startIndex + run.span - 1];
    return {
      claim: run.claim,
      startIndex: run.startIndex,
      span: run.span,
      isConflict: run.conflict,
      from,
      to,
      isBlockTop: true,
      isBlockBottom: true,
      ...describeClaim(run.claim, from, to, reservations),
    };
  });
}

/**
 * Turn a lane's bars into a full window of cells, filling the gaps between them.
 *
 * Free days stay one cell each, because their cell borders *are* the day gridlines the free track shows.
 * A blocked stretch becomes a single cell spanning the run: it is a range, like a bar, so one cell gives it
 * one fill and one tooltip instead of a seam and a repeated tooltip per day.
 */
function spreadLane(lane: ClaimBar[], dates: string[], blockedAt: (date: string) => BlockedReason | null): RowCell[] {
  const cells: RowCell[] = [];
  let column = 0;

  const gap = (until: number) => {
    while (column < until) {
      const reason = blockedAt(dates[column]);
      if (reason === null) {
        cells.push({ bar: null, blockedBy: null, span: 1 });
        column++;
        continue;
      }

      let span = 1;
      while (column + span < until && blockedAt(dates[column + span]) === reason) span++;
      cells.push({ bar: null, blockedBy: reason, span });
      column += span;
    }
  };

  for (const bar of lane) {
    gap(bar.startIndex);
    cells.push({ bar, blockedBy: spanReason(bar, dates, blockedAt), span: bar.span });
    column = bar.startIndex + bar.span;
  }
  gap(dates.length);

  return cells;
}

/**
 * The reason covering *every* night a bar holds, so a booking inside a closed period sits on the wash.
 *
 * Null when the nights disagree: one cell has one background, and a stay straddling the edge of a closure
 * cannot show the wash for only part of itself without going back to a second coordinate space. The lanes
 * beside it still carry the closure, so nothing is lost — and a stay is a few nights against a closure of
 * weeks, so wholly-in or wholly-out is the ordinary case.
 */
function spanReason(
  bar: ClaimBar,
  dates: string[],
  blockedAt: (date: string) => BlockedReason | null,
): BlockedReason | null {
  const reason = blockedAt(dates[bar.startIndex]);
  if (reason === null) return null;

  for (let offset = 1; offset < bar.span; offset++) {
    if (blockedAt(dates[bar.startIndex + offset]) !== reason) return null;
  }
  return reason;
}
